import { useEffect, useState } from "react";
import type { FrenzyStatus } from "../api/forage";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";

interface Props {
  status: FrenzyStatus | null;
  onFrenzy: () => void;
}

// The Frenzy card on the foraging side rail. A rectangular button
// whose background doubles as a loading bar:
//
//   * charging — bar fills left→right over the recharge window; the
//     button is disabled and reads "Charging…".
//   * ready    — bar full, button enabled, reads "Frenzy!".
//   * active   — bar drains right→left over the 15s boost window
//     (reverse-loading), button disabled.
//
// The server owns the state machine (and the actual ripening boost);
// this component only animates the bar locally between the phase
// pushes, driving each phase with a single linear CSS width
// transition seeded from the server's `remaining_ms`.
export function ForageFrenzyButton({ status, onFrenzy }: Props) {
  const theme = pixelTheme;
  const phase = status?.phase ?? "charging";

  // The bar fill: `pct` is the target width, `ms` the linear
  // transition time to reach it. We seed the start width with no
  // transition, then on the next frame set the target so the browser
  // animates between them.
  const [fill, setFill] = useState<{ pct: number; ms: number }>({
    pct: 0,
    ms: 0,
  });

  useEffect(() => {
    if (!status) {
      setFill({ pct: 0, ms: 0 });
      return;
    }

    if (status.phase === "ready") {
      setFill({ pct: 100, ms: 0 });
      return;
    }

    const d = status.duration_ms > 0 ? status.duration_ms : 1;
    const remaining = status.remaining_ms > 0 ? status.remaining_ms : 0;
    const elapsedPct = ((d - remaining) / d) * 100;
    // charging fills up toward 100%; active drains down toward 0%.
    const startPct =
      status.phase === "charging" ? clampPct(elapsedPct) : clampPct((remaining / d) * 100);
    const targetPct = status.phase === "charging" ? 100 : 0;

    // Seed the start instantly, then animate to the target across the
    // remaining time on the next frame.
    setFill({ pct: startPct, ms: 0 });
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setFill({ pct: targetPct, ms: remaining });
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [status]);

  const ready = phase === "ready";
  const label = phase === "charging" ? "Charging…" : "Frenzy!";
  const fillColor =
    phase === "charging" ? E32.amber : phase === "active" ? E32.pumpkin : E32.forest;

  return (
    <div style={{ ...simplePanelStyle(theme), padding: "14px 16px" }}>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          letterSpacing: "0.16em",
          color: theme.inkFaint,
          textTransform: "uppercase",
        }}
      >
        FRENZY
      </div>
      <button
        type="button"
        className="doi-btn"
        disabled={!ready}
        onClick={ready ? onFrenzy : undefined}
        aria-label={
          phase === "charging"
            ? "Frenzy charging"
            : phase === "active"
              ? "Frenzy active"
              : "Trigger Frenzy"
        }
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: 44,
          marginTop: 10,
          padding: 0,
          border: `2px solid ${theme.panelEdgeStrong}`,
          // Empty bar track behind the fill.
          background: E32.plum,
          overflow: "hidden",
          cursor: ready ? "pointer" : "default",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            right: "auto",
            width: `${fill.pct}%`,
            background: fillColor,
            transition: fill.ms > 0 ? `width ${fill.ms}ms linear` : "none",
          }}
        />
        <span
          style={{
            position: "relative",
            zIndex: 1,
            fontFamily: theme.fontDisplay,
            fontSize: 12,
            letterSpacing: "0.10em",
            color: E32.white,
            textShadow: `2px 2px 0 ${E32.ink}`,
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      </button>
    </div>
  );
}

function clampPct(v: number): number {
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}
